import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ContentStateInterface } from '../../../../@types/types'
import { InitState } from '../../../../redux/reducers/root/initialState'
import { fetchPriceMaximizerDataIfValid } from '../../../../redux/thunks/priceMaximizer/fetchPriceMaximizerDataIfValid'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { useFeatureSwitch } from '../../../../utils/features/FeatureSwitchContext'
import { FeatureSwitch } from '../../../../utils/features/featureSwitches'

import priceMaximizerStyle from './PriceMaximizer.module.css'
import { getDefaultLogger } from '../../../../services/logger/logger'

type PriceMaximizerProps = { insertionData: ContentStateInterface }
const PriceMaximizer: React.FC<PriceMaximizerProps> = (props) => {
  const { t } = useTranslation()

  const { similarObjects, savedSearches } = useAppSelector((state: InitState) => state.priceMaximizerFeature)

  const addressFeature = useAppSelector((state: InitState) => state.addressFeature)
  const flowInformation = useAppSelector((state: InitState) => state.flowFeature.flowInformation)

  const { isActive: useMyPropertyService } = useFeatureSwitch(FeatureSwitch.PPA_11933_USE_NEW_SUPPLY_DEMAND)

  const dispatch = useAppDispatch()

  useEffect(() => {
    const fetch = async () => {
      await dispatch(
        fetchPriceMaximizerDataIfValid(
          props.insertionData,
          addressFeature,
          flowInformation.validationSchema,
          useMyPropertyService
        )
      )
    }

    fetch().catch(getDefaultLogger().error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // having only the parts of insertionData and address listed as dependencies that are used in the fetchPriceMaximizerDataIfValid function
    props.insertionData.price,
    props.insertionData.additionalCosts,
    props.insertionData.heatingCosts,
    props.insertionData.room,
    props.insertionData.livingSpace,
    props.insertionData.plotArea,
    props.insertionData.manualCity?.value,
    props.insertionData.manualRegion?.value,
    props.insertionData.manualQuarter?.value,
    addressFeature.geoCoordinates.lambertEuCoordinatesX,
    addressFeature.geoCoordinates.lambertEuCoordinatesY,
    addressFeature.geoCoordinates.longitude,
    addressFeature.geoCoordinates.latitude,
    dispatch,
    useMyPropertyService,
  ])
  if (savedSearches === undefined || similarObjects === undefined) {
    return null
  }
  return (
    <div data-testid='price-maximizer-container' className={'grid-item margin-top-l'}>
      <div className={`grid-item ${priceMaximizerStyle['folding-section-wrap']}`}>
        <div className={`${priceMaximizerStyle['folding-section-header']}`}>
          <h6>{t('priceMaximizer.header', 'Price Maximizer')}</h6>
        </div>
        <div className={`${priceMaximizerStyle['circles-container']}`}>
          <div
            id='similar-objects'
            data-testid='similar-objects-testid'
            className={`${priceMaximizerStyle['single-circle-container']} ${priceMaximizerStyle['small-circle']}`}
          >
            <span>{similarObjects}</span>
            <label id='similar-objects-label' className='font-highlight' htmlFor='similar-objects'>
              <span>{t('priceMaximizer.similarObjectsLabel', 'Similar Objects')}</span>
            </label>
          </div>

          <div
            id='saved-searches'
            data-testid='saved-searches-testid'
            className={`${priceMaximizerStyle['single-circle-container']} ${priceMaximizerStyle['big-circle']}`}
          >
            <span>{savedSearches}</span>
            <label id='saved-searches-label' className='font-highlight' htmlFor='saved-searches'>
              <span>{t('priceMaximizer.savedSearchesLabel', 'Saved searches')}</span>
            </label>
          </div>
        </div>
        <div
          data-testid='tooltip-test-id'
          className={`${priceMaximizerStyle['saved-searches-tooltip']} ${priceMaximizerStyle['folding-tooltip']}`}
        >
          <strong>{savedSearches} </strong>
          {t('priceMaximizer.savedSearchesTooltip', 'saved searches tooltip')}
        </div>
      </div>
    </div>
  )
}

export default PriceMaximizer
